<script setup lang="ts">
import { Media } from "@/data/entities/media";
import formConfig from "./form_config";
import {
  formValuesToJSONData,
  JSONDataToFormValues,
} from "@/components/forms/functions";
import { toast } from "vue3-toastify";
import { createMediaStore } from "@/data/entities/media";
import { dialogEvents } from "@/data-views/dialogs";
import { FormValues } from "@/components/forms/types";

////////////////////////////////////////////////////////////
//////////////////// SETUP /////////////////////////////////
////////////////////////////////////////////////////////////
const mediaStore = createMediaStore();
const router = useRouter();

////////////////////////////////////////////////////////////
//////////////////// VARIABLES /////////////////////////////
////////////////////////////////////////////////////////////
const media = ref<Media>(new Media());
const loading = ref(false);
const value = ref<FormValues>({});
const isVisible = ref(false);
const id = ref<string | null>(null);

////////////////////////////////////////////////////////////
//////////////////// METHODS ///////////////////////////////
////////////////////////////////////////////////////////////
/**
 * Load the data from the store
 */
async function load(idParam?: string): Promise<void> {
  // Start loading
  loading.value = true;

  // Get the id from the param
  id.value =
    idParam ?? (router.currentRoute.value.query.mediaId as string | null);

  // Retrieve the item from the store
  if (id.value) {
    media.value = await mediaStore.retrieve(id.value).catch((error: Error) => {
      console.error(error);
      return new Media();
    });
  }

  // Apply values to the form
  value.value = JSONDataToFormValues(media.value.toJSON());

  // Stop loading
  loading.value = false;
}

/**
 * Submit the form
 */
async function submit(): Promise<void> {
  // Start loading
  loading.value = true;

  const json = formValuesToJSONData(value.value);

  // Apply to Media class
  const qContext = new Media();
  qContext.set(json);

  // Save the item
  await mediaStore
    .update(qContext)
    .then(() => {
      // Show success message
      toast.success("Media saved");
    })
    .catch((error: Error) => {
      console.error(error);
    });

  // Stop loading
  loading.value = false;

  // Close dialog
  isVisible.value = false;
}

////////////////////////////////////////////////////////////
//////////////////// LIFECYCLE /////////////////////////////
////////////////////////////////////////////////////////////
load();

onMounted(() => {
  window.addEventListener(
    dialogEvents.MediaFormDialog.OPEN,
    ({ detail }: any) => {
      // Load
      void load(detail.id);

      // Set the visibility to visible
      isVisible.value = true;
    },
  );
  window.addEventListener(dialogEvents.MediaFormDialog.CLOSE, () => {
    // Set the visibility to hidden
    isVisible.value = false;
  });
});
</script>

<template>
  <FormDialog
    :config="formConfig.dialog"
    :loading="loading"
    v-model="value"
    v-model:is-visible="isVisible"
    @submit="submit"
  />
</template>
